import { Controller } from "@hotwired/stimulus"

// The board's check-in quick answer: one modal per page, filled from the
// clicked cluster's data attributes, saved as ONE atomic PATCH to the
// quick_answers door (one audit row), chip cluster re-rendered via the
// turbo_stream response. Escape and backdrop CANCEL with no write (no
// blur-save wiring exists here by design, so the Escape-commits class of
// bug cannot recur). Registered on the board wrapper; openers are the
// cluster buttons.
//
// A music video is exempt from the weekly check-in (Ticket #1222; the
// opener's data-release-type against SharedRelease::CHECK_IN_EXEMPT_TYPES,
// handed in as a value): it opens with the three check-in questions HIDDEN
// under one line (design review 2026-10-05), leaving the No press release
// box, and its save OMITS all three keys. The door writes only what it is
// sent, so the stored answers are never cleared (committee MUST 2). The
// hidden selects are disabled too, so the focus trap never lands on them.
export default class extends Controller {
  static targets = ["modal", "heading", "subline", "s4a", "pitched",
                    "pitchedHelp", "distrokid", "dkHelp", "questions", "exemptLine",
                    "noPress", "error", "save", "opener"]
  static values = { checkInExempt: Array }

  open(event) {
    // The row navigates on click; the cluster opener stays in the cluster.
    event.stopPropagation()
    const d = event.currentTarget.dataset
    this.url = d.url
    this.headingTarget.textContent = d.releaseName
    this.sublineTarget.textContent = d.clientName
    this.exempt = this.checkInExemptValue.includes(d.releaseType || "")
    this.s4aTarget.value = this.exempt ? "" : (d.s4aStatus || "")
    this.pitchedTarget.value = this.exempt ? "" : (d.pitchedViaS4a || "")
    this.distrokidTarget.value = this.exempt ? "" : (d.distrokidPitch || "")
    // The DistroKid fine print names the client's distributor when that
    // is what answers the question (owner ruling 2026-09-07).
    this.dkHelpTarget.textContent = d.dkFinePrint
    // The "no press release" flag rides this modal; a changed flag moves
    // the row, so a change reloads the board after the save.
    if (this.hasNoPressTarget) {
      this.noPressTarget.checked = d.noPressRelease === "true"
      this.noPressWas = this.noPressTarget.checked
    }
    this.errorTarget.classList.add("hidden")
    this.saveTarget.disabled = false
    this.saveTarget.textContent = "Save answers"
    this.s4aChanged()

    this.previousFocus = document.activeElement
    this.modalTarget.hidden = false
    // A music video's questions are hidden, so it starts on the first
    // control still open (the No press release box, or Cancel).
    const first = this.exempt ? this.focusable()[0] : this.s4aTarget
    first?.focus()
    this.trapHandler = (e) => this.trapFocus(e)
    this.modalTarget.addEventListener("keydown", this.trapHandler)
  }

  close() {
    this.modalTarget.hidden = true
    this.modalTarget.removeEventListener("keydown", this.trapHandler)
    if (this.previousFocus) this.previousFocus.focus()
  }

  // Pitched is answerable only once uploaded (presentation of the MODEL
  // rule; the server enforces it regardless). A music video hides the
  // three questions under one line.
  s4aChanged() {
    const uploaded = this.s4aTarget.value === "uploaded"
    const exempt = !!this.exempt
    this.questionsTarget.hidden = exempt
    this.exemptLineTarget.hidden = !exempt
    this.s4aTarget.disabled = exempt
    this.pitchedTarget.disabled = exempt || !uploaded
    this.distrokidTarget.disabled = exempt
    this.pitchedHelpTarget.hidden = uploaded
  }

  save() {
    this.saveTarget.disabled = true
    this.saveTarget.textContent = "Saving..."
    this.errorTarget.classList.add("hidden")

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.url, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": token,
        "Content-Type": "application/json",
        "Accept": "text/vnd.turbo-stream.html"
      },
      body: JSON.stringify({
        // A music video sends none of the three check-in keys, so the
        // stored answers are left exactly as they were.
        ...(this.exempt ? {} : {
          s4a_status: this.s4aTarget.value,
          pitched_via_s4a: this.pitchedTarget.disabled ? null : this.pitchedTarget.value,
          distrokid_pitch: this.distrokidTarget.value
        }),
        // Only when the checkbox exists (non-interns): an absent key never
        // flips the flag server-side.
        ...(this.hasNoPressTarget ? { no_press_release: this.noPressTarget.checked } : {})
      })
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.text()
      })
      .then((stream) => {
        window.Turbo.renderStreamMessage(stream)
        const flagChanged = this.hasNoPressTarget && this.noPressTarget.checked !== this.noPressWas
        this.close()
        // The chips came back in the stream; the row's place in its day
        // did not, so a flipped flag reloads the board.
        if (flagChanged) window.Turbo.visit(window.location.href, { action: "replace" })
      })
      .catch(() => {
        this.errorTarget.classList.remove("hidden")
        this.saveTarget.disabled = false
        this.saveTarget.textContent = "Save answers"
      })
  }

  trapFocus(event) {
    if (event.key === "Escape") return this.close()
    if (event.key !== "Tab") return
    const focusable = this.focusable()
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault(); last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault(); first.focus()
    }
  }

  // Disabled controls cannot take focus, so they are never a wrap point
  // (a music video's three hidden selects are disabled).
  focusable() {
    return Array.from(this.modalTarget.querySelectorAll("select:not(:disabled), input:not(:disabled), button:not(:disabled)"))
  }
}
